import { onAuthStateChanged, reload, reauthenticateWithCredential, EmailAuthProvider, type Unsubscribe } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { userService } from './userService';
import { handleFirebaseError, createAuthError } from '../../lib/errors';
import { generateRandomAvatarUrl } from '../../lib/avatarUtils';
import { AuthErrorCode } from '../../types/auth';
import type { User, AuthResult } from '../../types/auth';

const isBrowser = typeof window !== 'undefined';

export const sessionService = {
  /**
   * Subscribe to auth state changes
   */
  onAuthStateChanged(callback: (user: User | null) => void): Unsubscribe {
    if (!isBrowser) {
      callback(null);
      return () => {};
    }

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        callback(null);
        return;
      }

      try {
        const user = await userService.convertFirebaseUser(firebaseUser);
        callback(user);
      } catch (error) {
        console.error('Error in auth state change:', error);
        // On permission-denied error, create a minimal user object
        // This allows the user to proceed even if Firestore is unavailable
        const isPermissionDenied = (error as any)?.code === 'permission-denied';
        if (isPermissionDenied) {
          console.warn('[sessionService] Permission denied - creating minimal user object');
          const { generateRandomAvatarUrl } = await import('../../lib/avatarUtils');
          const defaultAvatarUrl = generateRandomAvatarUrl(firebaseUser.email || firebaseUser.uid);
          const minimalUser: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Користувач',
            role: 'pending',
            emailVerified: firebaseUser.emailVerified,
            createdAt: new Date().toISOString(),
            settings: { theme: 'dark' },
            avatarUrl: defaultAvatarUrl
          };
          callback(minimalUser);
        } else {
          callback(null);
        }
      }
    });
  },

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    if (!isBrowser) return null;

    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;

    // Return basic user data synchronously
    // Full data should be fetched via query
    // Generate default avatar URL for quick access using predefined styles
    const defaultAvatarUrl = generateRandomAvatarUrl(firebaseUser.email || firebaseUser.uid);
    
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Користувач',
      role: 'pending',
      emailVerified: firebaseUser.emailVerified,
      createdAt: new Date().toISOString(),
      settings: { theme: 'dark' },
      avatarUrl: defaultAvatarUrl
    };
  },

  /**
   * Re-authenticate user with password
   */
  async reauthenticate(password: string): Promise<AuthResult<void>> {
    if (!isBrowser || !auth.currentUser || !auth.currentUser.email) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Get ID token
   */
  async getIdToken(forceRefresh = false): Promise<AuthResult<string>> {
    if (!isBrowser || !auth.currentUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      const token = await auth.currentUser.getIdToken(forceRefresh);
      return { success: true, data: token };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Refresh token
   */
  async refreshToken(): Promise<AuthResult<string>> {
    return this.getIdToken(true);
  },

  /**
   * Reload user data
   */
  async reloadUser(): Promise<AuthResult<void>> {
    if (!isBrowser || !auth.currentUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      await reload(auth.currentUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Check if session is valid
   */
  async isSessionValid(): Promise<boolean> {
    if (!isBrowser || !auth.currentUser) {
      return false;
    }

    try {
      const token = await auth.currentUser.getIdToken(false);
      return !!token;
    } catch {
      return false;
    }
  }
};
